import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, TextField, Button, Grid, Card, CardContent,
  Switch, FormControlLabel,
  CircularProgress, Chip, Alert, Divider, IconButton, Tooltip,
} from '@mui/material';
import {
  CloudDownload, CheckCircle, Error as ErrorIcon, Delete, Visibility,
  Warning, Download, History, Edit, Add, Close, ArrowBack,
} from '@mui/icons-material';
import { ftpConfigService } from '../services/api';

import { SUPPORTED_METRICS, METRIC_CATEGORIES } from '../config/sensorMetrics';

// Build grouped metrics for the column mapper dropdown
const METRICS_GROUPED = [
  { value: 'timestamp', label: 'Timestamp (mandatory)', group: 'Required' },
  ...SUPPORTED_METRICS.map(m => ({
    value: m.internal,
    label: `${m.userFriendly.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')} (${m.unit})`,
    group: m.category,
  })),
  { value: 'ignore', label: '— Ignore —', group: 'Other' },
];

const SCHEDULES = [
  { value: 'every_1h', label: 'Every 1 hour' },
  { value: 'every_6h', label: 'Every 6 hours' },
  { value: 'every_12h', label: 'Every 12 hours' },
  { value: 'every_24h', label: 'Every 24 hours' },
];

const DELIMITERS = [
  { value: ',', label: 'Comma (,)' },
  { value: ';', label: 'Semicolon (;)' },
  { value: '\t', label: 'Tab' },
  { value: '|', label: 'Pipe (|)' },
];

const SAMPLE_CSV = `timestamp,temperature_celsius,rh_percentage,rain_mm,wind_speed_m_per_s,atmospheric_pressure_mbar
2024-01-15 08:00:00,18.5,65.2,0.0,3.2,1013.5
2024-01-15 08:30:00,19.1,63.8,0.0,3.5,1013.3
2024-01-15 09:00:00,20.3,61.5,0.2,4.1,1013.1`;

type ViewMode = 'summary' | 'edit' | 'history';

interface FtpConfigSectionProps {
  stationUuid: string;
}

const FtpConfigSection: React.FC<FtpConfigSectionProps> = ({ stationUuid }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [hasConfig, setHasConfig] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('summary');

  // Config data (for summary display)
  const [configData, setConfigData] = useState<any>(null);

  // Form state
  const [host, setHost] = useState('');
  const [port, setPort] = useState(21);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [path, setPath] = useState('');
  const [delimiter, setDelimiter] = useState(',');
  const [schedule, setSchedule] = useState('every_24h');
  const [timezone, setTimezone] = useState('UTC');
  const [enabled, setEnabled] = useState(false);
  const [columnMapping, setColumnMapping] = useState<Record<string, string>>({});
  const [passwordSet, setPasswordSet] = useState(false);
  const [lastSync, setLastSync] = useState<string | null>(null);

  // Check result state
  const [checkResult, setCheckResult] = useState<any>(null);

  // Pull history
  const [pullHistory, setPullHistory] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => { loadConfig(); }, [stationUuid]);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const config = await ftpConfigService.get(stationUuid);
      setConfigData(config);
      populateForm(config);
      setHasConfig(true);
    } catch (err: any) {
      if (err.response?.status === 404) {
        setHasConfig(false);
        setConfigData(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const populateForm = (config: any) => {
    setHost(config.ftp_host);
    setPort(config.ftp_port);
    setUsername(config.ftp_username);
    setPath(config.ftp_path);
    setDelimiter(config.ftp_delimiter);
    setSchedule(config.ftp_schedule);
    setTimezone(config.ftp_timezone);
    setEnabled(config.ftp_enabled);
    setColumnMapping(config.ftp_file_format || {});
    setPasswordSet(config.ftp_password_set);
    setLastSync(config.ftp_last_sync);
  };

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const data = await ftpConfigService.history(stationUuid);
      setPullHistory(data);
      setViewMode('history');
    } catch (err) {
      console.error('Failed to load history:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setError('');
    setSuccess('');
    setCheckResult(null);
    try {
      const result = await ftpConfigService.test(stationUuid, {
        ftp_host: host, ftp_port: port,
        ftp_username: username, ftp_password: password || undefined,
        ftp_path: path, ftp_delimiter: delimiter,
        ftp_file_format: Object.keys(columnMapping).length > 0 ? columnMapping : undefined,
      });
      setCheckResult(result);
      // Auto-initialize column mapping if empty
      if (Object.keys(columnMapping).length === 0 && result.preview?.length > 0) {
        const initial: Record<string, string> = {};
        result.preview[0].forEach((_: string, idx: number) => {
          initial[String(idx)] = 'ignore';
        });
        setColumnMapping(initial);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    const hasTimestamp = Object.values(columnMapping).includes('timestamp');
    const hasMetric = Object.values(columnMapping).some(v => v !== 'timestamp' && v !== 'ignore');
    if (enabled && !hasTimestamp) { setError('Cannot enable: you must map one column as "Timestamp" (mandatory)'); return; }
    if (enabled && !hasMetric) { setError('Cannot enable: you must map at least one measurement column'); return; }
    if (!hasTimestamp && Object.keys(columnMapping).length > 0) { setError('Column mapping must include a Timestamp column'); return; }

    setSaving(true); setError(''); setSuccess('');
    try {
      await ftpConfigService.save(stationUuid, {
        ftp_host: host, ftp_port: port,
        ftp_username: username,
        ftp_password: password || undefined,
        ftp_path: path,
        ftp_file_format: columnMapping,
        ftp_delimiter: delimiter,
        ftp_schedule: schedule,
        ftp_timezone: timezone,
        ftp_enabled: enabled,
      });
      setSuccess('FTP configuration saved successfully');
      setHasConfig(true); setPasswordSet(true);
      await loadConfig();
      setViewMode('summary');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this FTP configuration? This will stop all automatic imports.')) return;
    try {
      await ftpConfigService.delete(stationUuid);
      setHasConfig(false); setConfigData(null);
      setHost(''); setPort(21); setUsername(''); setPassword('');
      setPath(''); setDelimiter(','); setSchedule('every_24h');
      setTimezone('UTC'); setEnabled(false); setColumnMapping({});
      setCheckResult(null); setPasswordSet(false); setLastSync(null);
      setViewMode('summary');
      setSuccess('FTP configuration deleted');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete');
    }
  };

  const handleCreate = () => {
    setHost(''); setPort(21); setUsername(''); setPassword('');
    setPath(''); setDelimiter(','); setSchedule('every_24h');
    setTimezone('UTC'); setEnabled(false); setColumnMapping({});
    setCheckResult(null); setPasswordSet(false); setLastSync(null);
    setError(''); setSuccess('');
    setViewMode('edit');
  };

  const handleDownloadSample = () => {
    const blob = new Blob([SAMPLE_CSV], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'sample_ftp_import.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const updateColumnMapping = useCallback((colIdx: string, metric: string) => {
    setColumnMapping(prev => ({ ...prev, [colIdx]: metric }));
  }, []);

  const getScheduleLabel = (val: string) => SCHEDULES.find(s => s.value === val)?.label || val;
  const getMappedMetricsCount = (mapping: Record<string, string>) => {
    return Object.values(mapping).filter(v => v !== 'ignore' && v !== 'timestamp').length;
  };

  if (loading) {
    return <Box sx={{ py: 3, textAlign: 'center' }}><CircularProgress size={24} /></Box>;
  }

  const hasTimestampMapped = Object.values(columnMapping).includes('timestamp');
  const hasMetricMapped = Object.values(columnMapping).some(v => v !== 'timestamp' && v !== 'ignore');
  const canEnable = hasTimestampMapped && hasMetricMapped;

  // ─── SUMMARY VIEW ───────────────────────────────────────────────
  if (viewMode === 'summary') {
    if (!hasConfig) {
      return (
        <Box>
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CloudDownload sx={{ fontSize: 48, color: 'rgba(255,255,255,0.2)', mb: 2 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              No FTP import configured for this station.
            </Typography>
            <Button variant="contained" startIcon={<Add />} onClick={handleCreate}
              sx={{ background: 'linear-gradient(135deg, #00d4ff, #0099cc)' }}>
              Create FTP Import
            </Button>
          </Box>
        </Box>
      );
    }

    const cfg = configData;
    return (
      <Box>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        <Card sx={{ background: 'rgba(0,0,0,0.15)', border: '1px solid rgba(255,255,255,0.08)' }}>
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            {/* Header row */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <CloudDownload sx={{ color: cfg?.ftp_enabled ? '#4caf50' : 'rgba(255,255,255,0.3)', fontSize: 22 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  FTP Import
                </Typography>
                <Chip
                  label={cfg?.ftp_enabled ? 'Active' : 'Disabled'}
                  size="small"
                  color={cfg?.ftp_enabled ? 'success' : 'default'}
                  variant={cfg?.ftp_enabled ? 'filled' : 'outlined'}
                  sx={{ fontSize: '0.7rem', height: 22 }}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Tooltip title="Edit Configuration">
                  <IconButton size="small" onClick={() => { setCheckResult(null); setError(''); setSuccess(''); setViewMode('edit'); }}
                    sx={{ color: 'rgba(255,255,255,0.5)', '&:hover': { color: '#00d4ff' } }}>
                    <Edit fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Pull History">
                  <IconButton size="small" onClick={loadHistory} disabled={historyLoading}
                    sx={{ color: 'rgba(255,255,255,0.5)', '&:hover': { color: '#ff9800' } }}>
                    {historyLoading ? <CircularProgress size={16} /> : <History fontSize="small" />}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete Configuration">
                  <IconButton size="small" onClick={handleDelete}
                    sx={{ color: 'rgba(255,255,255,0.5)', '&:hover': { color: '#f44336' } }}>
                    <Delete fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {/* Info grid */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 1.5 }}>
              <InfoItem label="Host" value={`${cfg?.ftp_host}:${cfg?.ftp_port}`} />
              <InfoItem label="Path" value={cfg?.ftp_path} />
              <InfoItem label="Schedule" value={getScheduleLabel(cfg?.ftp_schedule)} />
              <InfoItem label="Timezone" value={cfg?.ftp_timezone} />
              <InfoItem label="Metrics mapped" value={`${getMappedMetricsCount(cfg?.ftp_file_format || {})} columns`} />
              <InfoItem label="Created" value={cfg?.created_at ? new Date(cfg.created_at).toLocaleDateString() : '—'} />
              {cfg?.ftp_last_sync && (
                <InfoItem label="Last sync" value={new Date(cfg.ftp_last_sync).toLocaleString()} />
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // ─── HISTORY VIEW ───────────────────────────────────────────────
  if (viewMode === 'history') {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Button size="small" startIcon={<ArrowBack />} onClick={() => setViewMode('summary')}
            sx={{ color: 'rgba(255,255,255,0.7)' }}>
            Back
          </Button>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>
            Pull History (last 20)
          </Typography>
        </Box>

        {pullHistory.length === 0 ? (
          <Alert severity="info">No pull history yet.</Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {pullHistory.map((log: any) => (
              <Box key={log.id} sx={{
                display: 'flex', alignItems: 'center', gap: 1.5, p: 1,
                borderRadius: 1, background: 'rgba(0,0,0,0.15)',
              }}>
                {log.status === 'success' ? (
                  <CheckCircle sx={{ color: '#4caf50', fontSize: 18 }} />
                ) : (
                  <ErrorIcon sx={{ color: '#f44336', fontSize: 18 }} />
                )}
                <Typography variant="caption" sx={{ minWidth: 140 }}>
                  {new Date(log.timestamp).toLocaleString()}
                </Typography>
                <Chip label={log.status} size="small"
                  color={log.status === 'success' ? 'success' : 'error'} variant="outlined"
                  sx={{ fontSize: '0.7rem' }} />
                {log.records_sent > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    {log.records_sent} sent, {log.records_skipped} skipped
                  </Typography>
                )}
                {log.error_message && (
                  <Typography variant="caption" sx={{ color: '#f44336', flex: 1 }} noWrap>
                    {log.error_message}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        )}
      </Box>
    );
  }

  // ─── EDIT / CREATE VIEW ─────────────────────────────────────────
  return (
    <Box>
      {/* Header with back button */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Button size="small" startIcon={<ArrowBack />}
          onClick={() => { setViewMode('summary'); setCheckResult(null); setError(''); setSuccess(''); }}
          sx={{ color: 'rgba(255,255,255,0.7)' }}>
          Back
        </Button>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>
          {hasConfig ? 'Edit FTP Import' : 'Create FTP Import'}
        </Typography>
        <Button size="small" startIcon={<Download />} onClick={handleDownloadSample}
          sx={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)' }}>
          Sample CSV
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {/* Mandatory notice */}
      <Alert severity="info" sx={{ mb: 2, fontSize: '0.85rem' }}>
        <strong>Requirements:</strong> CSV must have max 10 columns. One column must be mapped as <strong>Timestamp</strong> (mandatory).
        Supported formats: YYYY-MM-DD HH:MM:SS, YYYY/MM/DD HH:MM:SS, DD/MM/YYYY HH:MM:SS.
        At least one measurement column is required.
      </Alert>

      {/* Connection Settings */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={8}>
          <TextField fullWidth size="small" label="FTP Host *" value={host}
            onChange={e => setHost(e.target.value)} placeholder="ftp.example.com" />
        </Grid>
        <Grid item xs={4}>
          <TextField fullWidth size="small" label="Port" type="number" value={port}
            onChange={e => setPort(parseInt(e.target.value) || 21)} />
        </Grid>
        <Grid item xs={6}>
          <TextField fullWidth size="small" label="Username *" value={username}
            onChange={e => setUsername(e.target.value)} />
        </Grid>
        <Grid item xs={6}>
          <TextField fullWidth size="small" label="Password *" type="password" value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder={passwordSet ? '••••••••' : ''}
            helperText={passwordSet ? 'Leave empty to keep current' : ''} />
        </Grid>
        <Grid item xs={12}>
          <TextField fullWidth size="small" label="Remote CSV File Path *" value={path}
            onChange={e => setPath(e.target.value)} placeholder="/data/readings.csv" />
        </Grid>
        <Grid item xs={4}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Delimiter</Typography>
          <select value={delimiter} onChange={e => setDelimiter(e.target.value)}
            style={{ width: '100%', padding: '8px', fontSize: '0.9rem', background: '#1a1a2e', color: '#fff', border: '1px solid rgba(255,255,255,0.23)', borderRadius: 4 }}>
            {DELIMITERS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
          </select>
        </Grid>
        <Grid item xs={4}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Schedule</Typography>
          <select value={schedule} onChange={e => setSchedule(e.target.value)}
            style={{ width: '100%', padding: '8px', fontSize: '0.9rem', background: '#1a1a2e', color: '#fff', border: '1px solid rgba(255,255,255,0.23)', borderRadius: 4 }}>
            {SCHEDULES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </Grid>
        <Grid item xs={4}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>CSV Timezone</Typography>
          <select value={timezone} onChange={e => setTimezone(e.target.value)}
            style={{ width: '100%', padding: '8px', fontSize: '0.9rem', background: '#1a1a2e', color: '#fff', border: '1px solid rgba(255,255,255,0.23)', borderRadius: 4 }}>
            <option value="UTC">UTC</option>
            <option value="Europe/Athens">Europe/Athens</option>
            <option value="Europe/London">Europe/London</option>
            <option value="Europe/Berlin">Europe/Berlin</option>
            <option value="Europe/Paris">Europe/Paris</option>
            <option value="Europe/Rome">Europe/Rome</option>
            <option value="Europe/Madrid">Europe/Madrid</option>
            <option value="Europe/Bucharest">Europe/Bucharest</option>
            <option value="Europe/Helsinki">Europe/Helsinki</option>
            <option value="US/Eastern">US/Eastern</option>
            <option value="US/Central">US/Central</option>
            <option value="US/Mountain">US/Mountain</option>
            <option value="US/Pacific">US/Pacific</option>
            <option value="Asia/Tokyo">Asia/Tokyo</option>
            <option value="Asia/Shanghai">Asia/Shanghai</option>
            <option value="Australia/Sydney">Australia/Sydney</option>
          </select>
        </Grid>
      </Grid>

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3 }}>
        <Button variant="outlined" startIcon={testing ? <CircularProgress size={16} /> : <Visibility />}
          onClick={handleTest} disabled={testing || !host || !username || !path || (!password && !passwordSet)}>
          {testing ? 'Testing...' : 'Test Connection & Preview'}
        </Button>
        <FormControlLabel
          control={<Switch checked={enabled} onChange={e => setEnabled(e.target.checked)} color="success" disabled={!canEnable} />}
          label={canEnable ? "Enable automatic import" : "Enable automatic import (map columns first)"}
        />
      </Box>

      {/* Check Results */}
      {checkResult && (
        <Card sx={{ mb: 3, background: 'rgba(0,0,0,0.1)' }}>
          <CardContent>
            {/* Connection info */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {checkResult.file}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Chip label={checkResult.connection_type} size="small" color="primary" variant="outlined" />
                <Chip label={`${checkResult.total_lines} lines`} size="small" />
                <Chip label={`${checkResult.column_count} columns`} size="small"
                  color={checkResult.column_count > 10 ? 'error' : 'default'} />
              </Box>
            </Box>

            {/* Validation errors */}
            {checkResult.errors?.length > 0 && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <strong>Validation Errors:</strong>
                <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
                  {checkResult.errors.map((e: string, i: number) => <li key={i}>{e}</li>)}
                </ul>
              </Alert>
            )}

            {/* Warnings */}
            {checkResult.warnings?.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {checkResult.warnings.map((w: string, i: number) => <div key={i}>{w}</div>)}
              </Alert>
            )}

            {/* Show either success OR errors — not both */}
            {checkResult.valid && checkResult.valid_rows > 0 ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                <strong>{checkResult.valid_rows} valid rows</strong> ready for import
                {checkResult.skipped_rows > 0 && ` (${checkResult.skipped_rows} skipped)`}
              </Alert>
            ) : (
              <>
                {checkResult.row_errors?.length > 0 && (
                  <Alert severity="warning" sx={{ mb: 2, maxHeight: 150, overflow: 'auto' }}>
                    <strong>Row Issues ({checkResult.skipped_rows} skipped):</strong>
                    <ul style={{ margin: '4px 0 0', paddingLeft: 20, fontSize: '0.85rem' }}>
                      {checkResult.row_errors.map((e: any, i: number) => (
                        <li key={i}>Row {e.row}: {e.error}</li>
                      ))}
                    </ul>
                  </Alert>
                )}
              </>
            )}

            {/* CSV Preview + Column Mapping */}
            {checkResult.preview?.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Column Mapping {!hasTimestampMapped && <Chip label="Timestamp not mapped!" size="small" color="error" sx={{ ml: 1 }} />}
                </Typography>
                <Box sx={{ overflowX: 'auto', mb: 1 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                    <thead>
                      <tr>
                        {checkResult.preview[0].map((_: string, colIdx: number) => (
                          <th key={colIdx} style={{ padding: '6px 8px', borderBottom: '2px solid rgba(255,255,255,0.1)', minWidth: 130 }}>
                            <select
                              value={columnMapping[String(colIdx)] || 'ignore'}
                              onChange={e => updateColumnMapping(String(colIdx), e.target.value)}
                              style={{
                                width: '100%', padding: '6px 4px', fontSize: '0.8rem',
                                background: columnMapping[String(colIdx)] === 'timestamp' ? '#1b5e20' : '#1a1a2e',
                                color: '#fff', border: `1px solid ${columnMapping[String(colIdx)] === 'timestamp' ? '#4caf50' : 'rgba(255,255,255,0.2)'}`,
                                borderRadius: 4, cursor: 'pointer',
                              }}
                            >
                              {(() => {
                                const groups: Record<string, typeof METRICS_GROUPED> = {};
                                METRICS_GROUPED.forEach(m => {
                                  if (!groups[m.group]) groups[m.group] = [];
                                  groups[m.group].push(m);
                                });
                                return Object.entries(groups).map(([group, items]) => (
                                  <optgroup key={group} label={group}>
                                    {items.map(m => (
                                      <option key={m.value} value={m.value}>{m.label}</option>
                                    ))}
                                  </optgroup>
                                ));
                              })()}
                            </select>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {checkResult.preview.map((row: string[], rowIdx: number) => (
                        <tr key={rowIdx} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                          {row.map((cell: string, colIdx: number) => (
                            <td key={colIdx} style={{ padding: '4px 8px', color: 'rgba(255,255,255,0.7)' }}>
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Map columns to metrics. The Timestamp column is <strong>mandatory</strong>. Unmapped columns are ignored. Max 10 columns.
                </Typography>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Save / Delete */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button variant="contained" onClick={handleSave}
          disabled={saving || !host || !username || !path}
          sx={{ background: 'linear-gradient(135deg, #00d4ff, #0099cc)' }}>
          {saving ? 'Saving...' : hasConfig ? 'Save Changes' : 'Create'}
        </Button>
        {hasConfig && (
          <Button variant="outlined" color="error" startIcon={<Delete />} onClick={handleDelete}>
            Delete
          </Button>
        )}
      </Box>
    </Box>
  );
};

// Helper component for summary info items
const InfoItem: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontSize: '0.85rem', wordBreak: 'break-all' }}>
      {value}
    </Typography>
  </Box>
);

export default FtpConfigSection;
